import React from 'react';
import { supabase } from '../../lib/supabase';
import { calcularRecorrentes, receitaDoMes, paraLancamento } from '../../utils/contabil/recorrentes';
import { ROXO, TEXTO, SUAVE, BORDA, VERDE, AMBAR, VERMELHO, fmtMoeda, botaoPrimario, botaoSecundario, botaoPequeno, th, td, desabilitado } from './comum';

// Lançamentos recorrentes do mês (Simples, pró-labore, INSS, honorários...).
// Ao abrir uma competência já encerrada, gera sozinho o que faltar (decisão da
// Bianca em 04/11/2026). O registro em contabil_recorrentes_gerados é gravado
// antes do lançamento: duas abas abertas não geram em dobro, e lançamento
// excluído à mão não volta sozinho — só pelo botão "Gerar de novo".

const ROTULO_CALCULO = {
  fixo: 'Valor fixo',
  percentual_receita: '% da receita do mês',
  percentual_de: '% de outro recorrente',
  restante_de: 'Outro recorrente menos deduções',
};

const VAZIO = { nome: '', historico: '', conta_debito: '', conta_credito: '', calculo: 'fixo', valor: '', percentual: '', base_id: '', deduz_ids: [], inicio: '', ativo: true, ordem: '' };

function mesAtual() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export default function RecorrentesPainel({
  empresaSlug, periodo, nomeMes, inicioLancamentos, mesFechado, lancamentosContabeis, contas,
  onGerado, setAviso, cardStyle, inputStyle, labelStyle,
}) {
  const [recorrentes, setRecorrentes] = React.useState(null);
  const [gerados, setGerados] = React.useState([]);
  const [configurando, setConfigurando] = React.useState(false);
  const [editando, setEditando] = React.useState(null); // { id|null, ...campos }
  const [salvando, setSalvando] = React.useState(false);
  const gerandoRef = React.useRef(false);

  const carregar = React.useCallback(async () => {
    if (!empresaSlug) return;
    const [r, g] = await Promise.all([
      supabase.from('contabil_recorrentes').select('*').eq('empresa_slug', empresaSlug).order('ordem'),
      supabase.from('contabil_recorrentes_gerados').select('recorrente_id, periodo, valor, lancamento_id, gerado_em').eq('empresa_slug', empresaSlug).eq('periodo', periodo),
    ]);
    if (r.error || g.error) {
      console.error('[recorrentes] Falha ao carregar.', r.error || g.error);
      setRecorrentes([]);
      return;
    }
    setRecorrentes(r.data || []);
    setGerados(g.data || []);
  }, [empresaSlug, periodo]);

  React.useEffect(() => { setRecorrentes(null); setGerados([]); carregar(); }, [carregar]);

  const receita = React.useMemo(() => receitaDoMes(lancamentosContabeis, periodo), [lancamentosContabeis, periodo]);
  const mapaGerados = React.useMemo(() => new Map(gerados.map(g => [g.recorrente_id, g])), [gerados]);
  const itens = React.useMemo(
    () => (recorrentes ? calcularRecorrentes(recorrentes, mapaGerados, periodo, receita) : []),
    [recorrentes, mapaGerados, periodo, receita],
  );

  const competenciaEncerrada = periodo < mesAtual();
  const podeGerar = !!empresaSlug && competenciaEncerrada && !mesFechado
    && (!inicioLancamentos || periodo >= inicioLancamentos.slice(0, 7));

  const gerar = React.useCallback(async (lista) => {
    if (!lista.length || gerandoRef.current) return;
    gerandoRef.current = true;
    try {
      // 1. Reserva a competência de cada recorrente (PK impede gerar em dobro).
      const { data: reservados, error: errRes } = await supabase.from('contabil_recorrentes_gerados')
        .upsert(lista.map(i => ({ recorrente_id: i.rec.id, periodo, empresa_slug: empresaSlug, valor: i.valor })), { onConflict: 'recorrente_id,periodo', ignoreDuplicates: true })
        .select('recorrente_id');
      if (errRes) throw errRes;
      const ids = new Set((reservados || []).map(r => r.recorrente_id));
      const aGravar = lista.filter(i => ids.has(i.rec.id));
      if (!aGravar.length) return;

      // 2. Lançamentos.
      const { data: lancs, error: errLanc } = await supabase.from('contabil_lancamentos')
        .upsert(aGravar.map(i => paraLancamento(i, { empresaSlug, periodo })), { onConflict: 'empresa_slug,chave_dedup', ignoreDuplicates: true })
        .select('id, chave_dedup');
      if (errLanc) {
        // Desfaz a reserva para tentar de novo na próxima abertura.
        await supabase.from('contabil_recorrentes_gerados').delete().eq('periodo', periodo).in('recorrente_id', [...ids]);
        throw errLanc;
      }

      // 3. Liga a reserva ao lançamento (para mostrar "excluído" depois).
      await Promise.all((lancs || []).map(l => {
        const recId = l.chave_dedup.split(':')[1];
        return supabase.from('contabil_recorrentes_gerados').update({ lancamento_id: l.id }).eq('recorrente_id', recId).eq('periodo', periodo);
      }));

      setAviso({ tipo: 'sucesso', texto: `${aGravar.length} lançamento(s) recorrente(s) de ${nomeMes} gerado(s): ${aGravar.map(i => `${i.rec.nome} ${fmtMoeda(i.valor)}`).join('; ')}.` });
      if (onGerado) onGerado();
    } catch (err) {
      console.error('[recorrentes] Falha ao gerar.', err);
      const m = (err && err.message) || '';
      setAviso({ tipo: 'erro', texto: /está fechado|anterior ao início da escrituração/.test(m) ? m : 'Não foi possível gerar os lançamentos recorrentes do mês. Tente novamente em instantes.' });
    } finally {
      gerandoRef.current = false;
      carregar();
    }
  }, [empresaSlug, periodo, nomeMes, onGerado, setAviso, carregar]);

  // Geração automática ao abrir a competência.
  React.useEffect(() => {
    if (!podeGerar || !recorrentes) return;
    const pendentes = itens.filter(i => i.situacao === 'gerar');
    if (pendentes.length) gerar(pendentes);
  }, [podeGerar, recorrentes, itens, gerar]);

  const gerarDeNovo = async (item) => {
    if (!window.confirm(`Gerar de novo "${item.rec.nome}" em ${nomeMes}?`)) return;
    const { error } = await supabase.from('contabil_recorrentes_gerados').delete().eq('recorrente_id', item.rec.id).eq('periodo', periodo);
    if (error) { setAviso({ tipo: 'erro', texto: 'Não foi possível liberar o recorrente para gerar de novo.' }); return; }
    await carregar();
  };

  // ── Configuração ────────────────────────────────────────────────────────
  const abrirEdicao = (rec) => setEditando(rec
    ? { ...rec, valor: rec.valor ?? '', percentual: rec.percentual ?? '', base_id: rec.base_id || '', deduz_ids: rec.deduz_ids || [], ordem: rec.ordem }
    : { ...VAZIO, inicio: periodo, ordem: (recorrentes || []).reduce((m, r) => Math.max(m, r.ordem), 0) + 1 });

  const salvar = async () => {
    const e = editando;
    const faltando = [
      !e.nome.trim() && 'Nome', !e.historico.trim() && 'Histórico', !e.conta_debito && 'Conta débito', !e.conta_credito && 'Conta crédito',
      !/^\d{4}-\d{2}$/.test(e.inicio) && 'Início',
      e.calculo === 'fixo' && !(parseFloat(e.valor) > 0) && 'Valor',
      ['percentual_receita', 'percentual_de'].includes(e.calculo) && !(parseFloat(e.percentual) > 0) && 'Percentual',
      ['percentual_de', 'restante_de'].includes(e.calculo) && !e.base_id && 'Recorrente base',
    ].filter(Boolean);
    if (faltando.length) { setAviso({ tipo: 'erro', texto: `Preencha: ${faltando.join(', ')}.` }); return; }
    if (e.conta_debito === e.conta_credito) { setAviso({ tipo: 'erro', texto: 'A conta de débito e a de crédito precisam ser diferentes.' }); return; }
    const registro = {
      empresa_slug: empresaSlug,
      ordem: parseInt(e.ordem, 10) || 0,
      nome: e.nome.trim(),
      historico: e.historico.trim(),
      conta_debito: e.conta_debito,
      conta_credito: e.conta_credito,
      calculo: e.calculo,
      valor: e.calculo === 'fixo' ? Math.round(parseFloat(e.valor) * 100) / 100 : null,
      percentual: ['percentual_receita', 'percentual_de'].includes(e.calculo) ? parseFloat(e.percentual) : null,
      base_id: ['percentual_de', 'restante_de'].includes(e.calculo) ? e.base_id : null,
      deduz_ids: e.calculo === 'restante_de' ? e.deduz_ids : [],
      inicio: e.inicio,
      ativo: !!e.ativo,
    };
    setSalvando(true);
    const { error } = e.id
      ? await supabase.from('contabil_recorrentes').update(registro).eq('id', e.id)
      : await supabase.from('contabil_recorrentes').insert(registro);
    setSalvando(false);
    if (error) {
      console.error('[recorrentes] Falha ao salvar.', error);
      setAviso({ tipo: 'erro', texto: 'Não foi possível salvar o recorrente. Tente novamente.' });
      return;
    }
    setEditando(null);
    setAviso({ tipo: 'sucesso', texto: 'Recorrente salvo. Lançamentos já gerados não mudam; o novo valor vale para as próximas competências geradas.' });
    carregar();
  };

  const excluir = async (rec) => {
    if (!window.confirm(`Excluir o recorrente "${rec.nome}"? Os lançamentos já gerados continuam no razão.`)) return;
    const { error } = await supabase.from('contabil_recorrentes').delete().eq('id', rec.id);
    if (error) { setAviso({ tipo: 'erro', texto: 'Não foi possível excluir o recorrente.' }); return; }
    carregar();
  };

  if (!recorrentes) return null;

  const corSituacao = { gerado: VERDE, gerar: ROXO, sem_valor: AMBAR, fora: SUAVE };
  const textoSituacao = (i) => {
    if (i.situacao === 'gerado') {
      const g = mapaGerados.get(i.rec.id);
      return g && !g.lancamento_id ? 'Gerado e depois excluído' : 'Gerado';
    }
    if (i.situacao === 'gerar') return podeGerar ? 'Gerando…' : (competenciaEncerrada ? 'Mês fechado' : 'Gera após o fim do mês');
    return i.motivo;
  };

  const set = (campo) => (ev) => setEditando(p => ({ ...p, [campo]: ev.target.type === 'checkbox' ? ev.target.checked : ev.target.value }));
  const outros = (recorrentes || []).filter(r => !editando || r.id !== editando.id);

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', alignItems: 'baseline' }}>
        <h4 style={{ color: TEXTO, margin: 0 }}>Lançamentos recorrentes — {nomeMes}</h4>
        <button onClick={() => { setConfigurando(c => !c); setEditando(null); }} style={botaoPequeno}>{configurando ? 'Fechar configuração' : 'Configurar'}</button>
      </div>
      <p style={{ fontSize: 12, color: SUAVE, margin: '4px 0 12px 0' }}>
        Provisões geradas sozinhas no último dia de cada mês encerrado, ao abrir o Fechamento. Receita do mês considerada: {fmtMoeda(receita)}.
      </p>

      {recorrentes.length === 0 ? (
        <div style={{ fontSize: 13, color: SUAVE }}>Nenhum lançamento recorrente configurado para esta empresa.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Recorrente</th>
                <th style={th}>Débito / Crédito</th>
                <th style={{ ...th, textAlign: 'right' }}>Valor</th>
                <th style={th}>Situação</th>
                {configurando && <th style={th}><span style={{ position: 'absolute', left: -9999 }}>Ações</span></th>}
              </tr>
            </thead>
            <tbody>
              {itens.map(i => (
                <tr key={i.rec.id}>
                  <td style={{ ...td, fontSize: 13 }}>
                    {i.rec.nome}
                    <div style={{ fontSize: 11, color: SUAVE }}>{ROTULO_CALCULO[i.rec.calculo]}{i.rec.percentual ? ` · ${Number(i.rec.percentual).toLocaleString('pt-BR')}%` : ''}</div>
                  </td>
                  <td style={{ ...td, fontSize: 12 }}>
                    <div style={{ color: VERMELHO }}>D {i.rec.conta_debito}</div>
                    <div style={{ color: VERDE }}>C {i.rec.conta_credito}</div>
                  </td>
                  <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', fontSize: 13, whiteSpace: 'nowrap' }}>{i.situacao === 'fora' ? '—' : fmtMoeda(i.valor)}</td>
                  <td style={{ ...td, fontSize: 12, color: corSituacao[i.situacao] }}>
                    {textoSituacao(i)}
                    {i.situacao === 'gerado' && !mapaGerados.get(i.rec.id)?.lancamento_id && !mesFechado && (
                      <div><button onClick={() => gerarDeNovo(i)} style={{ ...botaoPequeno, marginTop: 4 }}>Gerar de novo</button></div>
                    )}
                  </td>
                  {configurando && (
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      <button onClick={() => abrirEdicao(i.rec)} style={botaoPequeno} aria-label={`Editar ${i.rec.nome}`}>✏️</button>{' '}
                      <button onClick={() => excluir(i.rec)} style={botaoPequeno} aria-label={`Excluir ${i.rec.nome}`}>🗑</button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {configurando && !editando && (
        <button onClick={() => abrirEdicao(null)} style={{ ...botaoSecundario, marginTop: 10 }}>+ Novo recorrente</button>
      )}

      {editando && (
        <div style={{ marginTop: 12, border: `1px solid ${ROXO}`, borderRadius: 8, padding: 12, background: 'rgba(124,58,237,0.03)' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: ROXO, marginBottom: 10 }}>{editando.id ? `Editar ${editando.nome}` : 'Novo recorrente'}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
            <Campo id="rc-nome" rotulo="Nome" labelStyle={labelStyle}><input id="rc-nome" style={inputStyle} value={editando.nome} onChange={set('nome')} placeholder="Ex.: Honorários contábeis a pagar" /></Campo>
            <Campo id="rc-hist" rotulo="Histórico ({competencia} vira MM/AAAA)" labelStyle={labelStyle}><input id="rc-hist" style={inputStyle} value={editando.historico} onChange={set('historico')} placeholder="Provisão honorários {competencia}" /></Campo>
            <Campo id="rc-deb" rotulo="Conta débito" labelStyle={labelStyle}>
              <select id="rc-deb" style={inputStyle} value={editando.conta_debito} onChange={set('conta_debito')}>
                <option value="">— Selecione —</option>
                {contas.map(c => <option key={c.codigo} value={c.rotulo}>{c.rotulo}</option>)}
              </select>
            </Campo>
            <Campo id="rc-cred" rotulo="Conta crédito" labelStyle={labelStyle}>
              <select id="rc-cred" style={inputStyle} value={editando.conta_credito} onChange={set('conta_credito')}>
                <option value="">— Selecione —</option>
                {contas.map(c => <option key={c.codigo} value={c.rotulo}>{c.rotulo}</option>)}
              </select>
            </Campo>
            <Campo id="rc-calc" rotulo="Cálculo do valor" labelStyle={labelStyle}>
              <select id="rc-calc" style={inputStyle} value={editando.calculo} onChange={set('calculo')}>
                {Object.entries(ROTULO_CALCULO).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Campo>
            {editando.calculo === 'fixo' && (
              <Campo id="rc-valor" rotulo="Valor (R$)" labelStyle={labelStyle}><input id="rc-valor" type="number" step="0.01" min="0" style={inputStyle} value={editando.valor} onChange={set('valor')} /></Campo>
            )}
            {['percentual_receita', 'percentual_de'].includes(editando.calculo) && (
              <Campo id="rc-perc" rotulo="Percentual (%)" labelStyle={labelStyle}><input id="rc-perc" type="number" step="0.01" min="0" style={inputStyle} value={editando.percentual} onChange={set('percentual')} /></Campo>
            )}
            {['percentual_de', 'restante_de'].includes(editando.calculo) && (
              <Campo id="rc-base" rotulo="Recorrente base" labelStyle={labelStyle}>
                <select id="rc-base" style={inputStyle} value={editando.base_id} onChange={set('base_id')}>
                  <option value="">— Selecione —</option>
                  {outros.map(r => <option key={r.id} value={r.id}>{r.nome}</option>)}
                </select>
              </Campo>
            )}
            <Campo id="rc-inicio" rotulo="Primeira competência" labelStyle={labelStyle}><input id="rc-inicio" type="month" style={inputStyle} value={editando.inicio} onChange={set('inicio')} /></Campo>
            <Campo id="rc-ordem" rotulo="Ordem" labelStyle={labelStyle}><input id="rc-ordem" type="number" min="0" style={inputStyle} value={editando.ordem} onChange={set('ordem')} /></Campo>
          </div>
          {editando.calculo === 'restante_de' && (
            <fieldset style={{ border: `1px solid ${BORDA}`, borderRadius: 8, marginTop: 10, padding: '8px 12px' }}>
              <legend style={{ fontSize: 12, color: SUAVE }}>Deduzir do valor base</legend>
              {outros.filter(r => r.id !== editando.base_id).map(r => (
                <label key={r.id} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, color: TEXTO }}>
                  <input type="checkbox" checked={editando.deduz_ids.includes(r.id)}
                    onChange={ev => setEditando(p => ({ ...p, deduz_ids: ev.target.checked ? [...p.deduz_ids, r.id] : p.deduz_ids.filter(x => x !== r.id) }))} />
                  {r.nome}
                </label>
              ))}
            </fieldset>
          )}
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, color: TEXTO, marginTop: 10 }}>
            <input type="checkbox" checked={!!editando.ativo} onChange={set('ativo')} /> Ativo
          </label>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
            <button onClick={() => setEditando(null)} style={botaoSecundario}>Cancelar</button>
            <button onClick={salvar} disabled={salvando} style={desabilitado(botaoPrimario, salvando)}>{salvando ? 'Salvando…' : 'Salvar'}</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Campo({ id, rotulo, labelStyle, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
      <label htmlFor={id} style={labelStyle}>{rotulo}</label>
      {children}
    </div>
  );
}
